import Polygon from 'ol/geom/Polygon';

const isMobileViewport = window.matchMedia('(max-width: 900px)').matches;

export function createCurvedSegmentCoordinates(startCoord, endCoord, segmentIndex) {
  const [x1, y1] = startCoord;
  const [x2, y2] = endCoord;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return [startCoord, endCoord];
  }

  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const nx = -dy / length;
  const ny = dx / length;
  const bendDirection = segmentIndex % 2 === 0 ? 1 : -1;
  const bendAmount = Math.min(14000, Math.max(2000, length * 0.06));
  const cx = mx + nx * bendAmount * bendDirection;
  const cy = my + ny * bendAmount * bendDirection;

  const points = [];
  const steps = 18;
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const omt = 1 - t;
    const px = (omt * omt * x1) + (2 * omt * t * cx) + (t * t * x2);
    const py = (omt * omt * y1) + (2 * omt * t * cy) + (t * t * y2);
    points.push([px, py]);
  }
  return points;
}

export function getArrowPlacementAtFraction(curveCoords, fractionFromStart) {
  if (!curveCoords || curveCoords.length < 2) {
    return {
      coord: curveCoords && curveCoords[0] ? curveCoords[0] : [0, 0],
      angle: 0
    };
  }

  let totalLength = 0;
  for (let i = 1; i < curveCoords.length; i += 1) {
    const dx = curveCoords[i][0] - curveCoords[i - 1][0];
    const dy = curveCoords[i][1] - curveCoords[i - 1][1];
    totalLength += Math.hypot(dx, dy);
  }

  const targetDistance = totalLength * Math.max(0, Math.min(1, fractionFromStart));
  let traveled = 0;

  for (let i = 1; i < curveCoords.length; i += 1) {
    const tail = curveCoords[i - 1];
    const head = curveCoords[i];
    const segDx = head[0] - tail[0];
    const segDy = head[1] - tail[1];
    const segLength = Math.hypot(segDx, segDy);
    if (segLength === 0) {
      continue;
    }

    if (traveled + segLength >= targetDistance) {
      const remaining = targetDistance - traveled;
      const ratio = remaining / segLength;
      const coord = [
        tail[0] + (head[0] - tail[0]) * ratio,
        tail[1] + (head[1] - tail[1]) * ratio
      ];
      return {
        coord,
        angle: Math.atan2(segDy, segDx)
      };
    }

    traveled += segLength;
  }

  const fallbackTail = curveCoords[0];
  const fallbackHead = curveCoords[1];
  return {
    coord: fallbackHead,
    angle: Math.atan2(fallbackHead[1] - fallbackTail[1], fallbackHead[0] - fallbackTail[0])
  };
}

export function createArrowHeadGeometry(coord, angle) {
  const size = isMobileViewport ? 650 : 500;
  const spread = Math.PI / 7;
  const [x, y] = coord;

  const left = [
    x - size * Math.cos(angle - spread),
    y - size * Math.sin(angle - spread)
  ];
  const right = [
    x - size * Math.cos(angle + spread),
    y - size * Math.sin(angle + spread)
  ];

  return new Polygon([[coord, left, right, coord]]);
}
